import { RefObject, useEffect, useCallback } from 'react';

// Track active elements that should be considered "inside"
const activeElements = new Map<string, HTMLElement>();
let nextId = 0;

export const useClickOutside = (
  ref: RefObject<HTMLElement | null>,
  handler: (event: MouseEvent | TouchEvent) => void
) => {
  // Create a stable callback that won't cause effect to re-run
  const stableHandler = useCallback(handler, [handler]);

  useEffect(() => {
    if (!ref.current) return;

    // Generate a unique ID for this element
    const id = `click-outside-${nextId++}`;

    // Mark this element as active
    activeElements.set(id, ref.current);

    // Add a data attribute for debugging
    ref.current.setAttribute('data-click-outside-id', id);

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // Get the target element
      const target = event.target as Node;

      // Check if the click is inside any of our tracked elements
      for (const element of activeElements.values()) {
        if (element === target || element.contains(target)) {
          return; // Click was inside, do nothing
        }
      }

      // If we got here, the click was outside all tracked elements
      stableHandler(event);
    };

    // Use capture phase to get events before they're stopped
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);

    // Clean up
    return () => {
      activeElements.delete(id);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [ref, stableHandler]);
}; 